import { CONTAINER_KIND_LABELS, type ContainerKind } from '@/constants/ds-attendance.constants'

/** Um chip "Filtros aplicados (somente leitura)" do modal de exportação. */
export interface ExportFilterChip {
  label: string
  value: string
}

/**
 * Entrada do helper: rótulos JÁ resolvidos pela fila (nome da unidade, da escala, do
 * turno, do status, do motivo…), nunca ids nem refs. Manter o helper puro é o que
 * permite testá-lo caso a caso e deixa o `computed` da fila com complexidade 1 — a
 * versão inline, com um spread por filtro, estourava `complexity: 10`.
 */
export interface ExportFilterChipsInput {
  /** Aba "Solicitações" (recorte próprio, `onlyRequests` do backend). */
  onlyRequests: boolean
  /** Aba "Folha"/"Atividade"; ausente = "Todos", que não é filtro. */
  kind: ContainerKind | null | undefined
  /** `false` na aba "Solicitações": o dropdown some e o backend ignora o status. */
  showStatusFilter: boolean
  statusLabels: string[]
  healthUnitLabels: string[]
  scheduleLabels: string[]
  shiftLabels: string[]
  activityTypeLabel: string | null
  /** "Data plantão" já formatada (`dd/MM/yyyy - dd/MM/yyyy`). */
  dateLabel: string | null
  /** "Data upload" já formatada — o modal NÃO a converte em período. */
  uploadDateLabel: string | null
  onlyDivergent: boolean
  onlyAntecipe: boolean
  onlyInCash: boolean
  onlyStationChanged: boolean
  manualReviewReasonLabels: string[]
  includeNoScheduleExpected: boolean
}

/**
 * Copy dos toggles da fila (`Somente com divergência`, `Com Antecipe`, `Com À vista`,
 * `Plantão alterado após auditoria`) sob o rótulo "Marcador" — o chip repete o que o
 * usuário leu no filtro, não inventa um nome novo para o mesmo recorte. "Sem escala
 * prevista" é o único que INCLUI registros em vez de recortar, e o rótulo diz isso.
 */
const TOGGLE_CHIPS = {
  divergent: { label: 'Marcador', value: 'Somente com divergência' },
  antecipe: { label: 'Marcador', value: 'Com Antecipe' },
  inCash: { label: 'Marcador', value: 'Com À vista' },
  stationChanged: { label: 'Marcador', value: 'Plantão alterado após auditoria' },
  noScheduleExpected: { label: 'Inclui', value: 'Registros sem escala prevista' }
} as const satisfies Record<string, ExportFilterChip>

const many = (label: string, values: string[]): ExportFilterChip[] =>
  values.map((value) => ({ label, value }))

const one = (label: string, value: string | null): ExportFilterChip[] =>
  value ? [{ label, value }] : []

const when = (enabled: boolean, chip: ExportFilterChip): ExportFilterChip[] =>
  enabled ? [chip] : []

function tabChip(input: ExportFilterChipsInput): ExportFilterChip[] {
  if (input.onlyRequests) return [{ label: 'Aba', value: 'Solicitações' }]
  return input.kind ? [{ label: 'Aba', value: CONTAINER_KIND_LABELS[input.kind] }] : []
}

/**
 * Os chips somente leitura do modal de exportação, na ordem em que a fila apresenta
 * os filtros: aba, status, unidade, escala, turno, tipo, "Data plantão", "Data upload",
 * marcadores, motivo da revisão e "sem escala prevista". Filtro desligado não gera
 * chip. Um filtro novo na fila precisa entrar AQUI também — é o que faz a lista de
 * chips prometer o mesmo recorte que `buildQueueFilters` manda ao arquivo.
 */
export function buildExportFilterChips(input: ExportFilterChipsInput): ExportFilterChip[] {
  return [
    ...tabChip(input),
    ...(input.showStatusFilter ? many('Status', input.statusLabels) : []),
    ...many('Unidade', input.healthUnitLabels),
    ...many('Escala', input.scheduleLabels),
    ...many('Turno', input.shiftLabels),
    ...one('Tipo', input.activityTypeLabel),
    ...one('Data plantão', input.dateLabel),
    ...one('Data upload', input.uploadDateLabel),
    ...when(input.onlyDivergent, TOGGLE_CHIPS.divergent),
    ...when(input.onlyAntecipe, TOGGLE_CHIPS.antecipe),
    ...when(input.onlyInCash, TOGGLE_CHIPS.inCash),
    ...when(input.onlyStationChanged, TOGGLE_CHIPS.stationChanged),
    ...many('Motivo da revisão', input.manualReviewReasonLabels),
    ...when(input.includeNoScheduleExpected, TOGGLE_CHIPS.noScheduleExpected)
  ]
}
